<template>
  <AppDialog :is-open="isDialogOpened"
             :hideFooter="redactDocumentPending"
             :closeButton="!redactDocumentPending"
             size="l"
             @cancel="closeDialog"
  >
    <template v-if="!redactDocumentPending" #title>
      {{ $t(`${redactDialogTranslationKey}.title`) }}
    </template>

    <template #body>
      <p class="my-4" v-if="!redactDocumentPending">
        {{ $t(`${redactDialogTranslationKey}.description`) }}
      </p>

      <div v-if="redactDocumentPending" class="text-center">
        <AppLoader small color="info-darken-1"></AppLoader>
        <h4 class="mt-6">
          {{ $t('project.documents.DocumentViewer.RedactDocumentDialog.savingInProgress') }}
        </h4>
        <p class="my-6">
          {{ $t('project.documents.DocumentViewer.RedactDocumentDialog.actionCanTakeSomeTime') }}
        </p>
      </div>
    </template>

    <template v-if="!redactDocumentPending" #footer>
      <template v-if="preventRedactPaneClose">
        <app-button
          type="outlined"
          variant="neutral"
          @click="quitWithoutSaving"
        >
          {{ $t('project.documents.DocumentViewer.RedactDocumentDialog.quitWithoutSaving') }}
        </app-button>

        <v-spacer />
      </template>

      <app-button
        class="mr-2"
        type="outlined"
        variant="neutral"
        @click="closeDialog"
      >
        {{ $t('common.cancel') }}
      </app-button>

      <app-button
        @click="prepareRedactDocument"
      >
        {{ $t('common.register') }}
      </app-button>
    </template>
  </AppDialog>
</template>

<script>
import { mapActions } from 'vuex'

import AppLoader from '@/common/AppLoader.vue'
import AppDialog from '@/common/dialogs/AppDialog.vue'
import { track } from '@/common/pendo/agent'
import { toast } from '@/common/utils/toast'
import { dialogMixin } from '@/mixins/dialogMixin'
import { DocumentViewerActionTypes } from '@/store/modules/document-viewer/action_types'
import { GET_FILE } from '@/store/modules/files/action_types'

export default {
  name: 'RedactDocumentDialog',
  components: { AppDialog, AppLoader },
  mixins: [dialogMixin],
  emits: [
    'confirm',
    'quit-without-saving',
    'polling-error',
  ],
  props: {
    pdfAppInstance: {
      type: Object,
      required: true,
    },
    mnemo: {
      type: String,
      required: true,
    },
    fileId: {
      type: [String, Number],
      required: true,
    },
    redactedZones: {
      type: Array,
      required: true,
    },
    isRedactByZone: {
      type: Boolean,
      default: false,
    },
    preventRedactPaneClose: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      pdfContainer: this.pdfAppInstance.pdfViewer.container,
      redactDocumentPending: false,
      pollingInterval: null,
    }
  },
  computed: {
    redactDialogTranslationKey () {
      return `project.documents.DocumentViewer.RedactDocumentDialog${this.preventRedactPaneClose ? '.quit' : ''}`;
    },
  },
  methods: {
    ...mapActions('files', [GET_FILE]),
    ...mapActions('documentViewer', [DocumentViewerActionTypes.REDACT_DOCUMENT]),
    async prepareRedactDocument () {
      this.redactDocumentPending = true
      this.$emit('polling-error', null)

      try {
        await this.REDACT_DOCUMENT({
          mnemo: this.mnemo,
          fileId: this.fileId,
          data: {
            rectanglePositions: this.convertCanvasRectanglesToPdf(),
          },
        })
        track('redact_started')
        if (this.isRedactByZone) {
          track('redact_by_zone')
        } else {
          track('redact_by_keywords')
        }

        // Start polling after the document is sent for redaction
        // Because the redaction process is asynchronous on the server
        this.startPolling()
      } catch (error) {
        // No toast here because the error message is displayed in the parent's template
        console.error(error)
        this.redactDocumentPending = false
        this.closeDialog()
      }
    },
    startPolling() {
      if (this.pollingInterval) return
      // Declared here so it can be checked in the finally clause out of the try-catch block
      let updatedFile
      this.pollingInterval = setInterval(async () => {
        try {
          updatedFile = await this.GET_FILE({ id: this.fileId, mnemo: this.mnemo })

          if (updatedFile.isRedacted) {
            toast.success(this.$t('project.documents.DocumentViewer.RedactDocumentDialog.redactDocumentSuccess'))
            this.$emit('confirm')
          } else if (updatedFile.isRedactionFailed) {
            console.error('Redaction failed on the server')
            this.$emit('polling-error', true)
          }
        } catch (e) {
          console.error('Redaction failed:', e);
          this.$emit('polling-error', e)
        } finally {
          if (!updatedFile.isRedacting) {
            this.stopPolling()
            this.closeDialog()
          }
        }
      }, 5000) // Check every 5 seconds
    },
    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval)
        this.pollingInterval = null
      }
      this.redactDocumentPending = false
    },
    convertCanvasRectanglesToPdf () {
      this.createTemporaryCanvases()

      const convertedZones = this.redactedZones.map(rect => {
        const pageElement = this.pdfContainer.querySelector(`.page[data-page-number="${rect.page}"]`)
        if (!pageElement) return null

        const canvas = pageElement.querySelector('.annotation-canvas')
        if (!canvas) return null

        // Retrieve dimensions of the PdfViewer and the canvas
        const pdfViewerPage = this.pdfAppInstance.pdfViewer.getPageView(rect.page - 1)
        const viewport = pdfViewerPage.pdfPage.getViewport({ scale: 1 })
        const pdfWidth = viewport.width
        const pdfHeight = viewport.height
        const canvasWidth = canvas.width
        const canvasHeight = canvas.height

        // Convert canvas coordinates to Pdf coordinates
        const scaleFactorX = pdfWidth / canvasWidth
        const scaleFactorY = pdfHeight / canvasHeight

        return {
          page: rect.page,
          x: rect.x * canvasWidth * scaleFactorX,
          y: rect.y * canvasHeight * scaleFactorY,
          endX: (rect.x + rect.width) * canvasWidth * scaleFactorX,
          endY: (rect.y + rect.height) * canvasHeight * scaleFactorY,
        }
      }).filter(Boolean)

      this.removeTemporaryCanvases()

      return convertedZones
    },
    createTemporaryCanvases() {
      // Create a canvas for each page that doesn't have one
      // This is necessary because the canvas is removed when the page is not visible
      // and we need it to convert the rectangles to Pdf coordinates
      const pages = this.pdfContainer.querySelectorAll('.page')
      pages.forEach((pageElement) => {
        if (!pageElement.querySelector('.annotation-canvas')) {
          const canvas = document.createElement('canvas')
          canvas.className = 'annotation-canvas'
          canvas.style.position = 'absolute'
          canvas.style.top = '0'
          canvas.style.left = '0'
          canvas.width = pageElement.clientWidth
          canvas.height = pageElement.clientHeight

          pageElement.appendChild(canvas)
        }
      })
    },
    removeTemporaryCanvases() {
      const pages = this.pdfContainer.querySelectorAll('.page')
      pages.forEach((pageElement) => {
        const boundingRect = pageElement.getBoundingClientRect()
        // Check if page is not visible in viewport and remove the canvas
        const isPageVisible = boundingRect.top < this.pdfContainer.clientHeight && boundingRect.bottom > 0
        if (!isPageVisible) {
          const canvas = pageElement.querySelector('.annotation-canvas')
          if (canvas) {
            pageElement.removeChild(canvas)
          }
        }
      })
    },
    quitWithoutSaving () {
      this.$emit('quit-without-saving')
      this.closeDialog()
    },
  },
}
</script>
